/**
 * Offline-tolerant POD upload queue.
 *
 * Persists driver POD / Fail submissions to IndexedDB so they survive page
 * reloads, app backgrounding on iOS Safari, and intermittent connectivity
 * (truck in a basement, underground garage, dead-zone Walmart parking lot).
 *
 * Why IndexedDB and not localStorage?
 *   localStorage is strings only — Blobs would need base64 encoding
 *   (≈ 33% size penalty) and the 5MB hard limit would cap us at ~3.5 MB of
 *   actual photos. IndexedDB stores native Blobs and has multi-GB quota.
 *
 * Queue entries:
 *   {
 *     id            string  uuid for this attempt
 *     order_id      string
 *     endpoint      "/orders/<id>/deliver" | "/orders/<id>/fail"
 *     parts         { [k: string]: string | Blob | File }
 *     status        "pending" | "uploading" | "failed"
 *     attempts      number
 *     last_error    string | null
 *     queued_at     ISO timestamp
 *     last_try_at   ISO timestamp | null
 *   }
 *
 * Consumers register a single drain callback via `attachUploader(uploaderFn)`.
 * The queue calls it for each pending item; the uploader resolves on success
 * and rejects on failure (we catch and keep the item for the next sweep).
 */
import { get, set, del, keys } from "idb-keyval";

const STORE_PREFIX = "pod-q:"; // each item is one key in the default store
// Dropped items are moved here (never nuked outright) so support can recover
// delivery records that couldn't be uploaded after all retries were exhausted.
const GRAVEYARD_PREFIX = "pod-q-dead:";
const SWEEP_MS = 30_000;

function uid() {
  // Lightweight UUID — good enough for client-side de-duplication.
  return "p-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

export async function enqueue({ order_id, endpoint, parts, requireSignature = true }) {
  // PROD INCIDENT (2026-07-15): items were queued with a valid `parts` map
  // but by the time the sweeper drained (sometimes after an app restart on
  // iOS), the stored copy had lost its contents — Blobs came back as empty
  // and even plain-string `comment` was gone. The backend saw a body with
  // "missing: Photo 1, Photo 2, Signature, Comments" and 400'd on every
  // retry. Fix has two layers:
  //   1. Convert every Blob/File to a plain {ab: ArrayBuffer, ct, name} tuple
  //      before storing to IDB. ArrayBuffers survive the structured-clone +
  //      quota-eviction round-trip reliably across Chrome, Safari, Firefox.
  //      Blobs do not — Safari's WebKit is known to evict Blob-backed IDB
  //      entries under memory pressure or after a tab close.
  //   2. Validate the round-trip: read the item back out of IDB after write
  //      and verify every required field is still populated. If IDB silently
  //      corrupted the payload we surface it at capture time so the driver
  //      retries immediately with the sheet still open (much better UX than
  //      a stuck queue an hour later).
  const stableParts = await _partsToStorable(parts);
  if (endpoint && endpoint.endsWith("/deliver")) {
    const shape = _describeStoredDeliverParts(stableParts, requireSignature);
    if (shape.missing.length) {
      throw new Error(`Cannot queue delivery — missing fields at capture: ${shape.missing.join(", ")}`);
    }
  }
  const item = {
    id: uid(),
    order_id,
    endpoint,
    parts: stableParts,
    // Pickup-type orders have no recipient to sign.
    require_signature: requireSignature,
    status: "pending",
    attempts: 0,
    last_error: null,
    queued_at: new Date().toISOString(),
    last_try_at: null,
  };
  await set(STORE_PREFIX + item.id, item);
  // Post-write round-trip check. If IDB silently dropped the buffers we
  // surface the error so the sheet can stay open and the driver retries.
  if (endpoint && endpoint.endsWith("/deliver")) {
    try {
      const echoed = await get(STORE_PREFIX + item.id);
      const echoShape = _describeStoredDeliverParts(echoed?.parts, requireSignature);
      if (echoShape.missing.length) {
        await del(STORE_PREFIX + item.id);
        throw new Error(
          `Delivery capture was lost by browser storage (missing after save: ${echoShape.missing.join(", ")}). Please retake the POD and try again.`
        );
      }
    } catch (e) {
      if (e instanceof Error && e.message.startsWith("Delivery capture was lost")) throw e;
    }
  }
  return item.id;
}

// Convert a raw {key: string | Blob | File} parts map into a persistence-safe
// {key: string | {ab: ArrayBuffer, ct, name}} map. ArrayBuffers are cloned by
// value by the structured-clone algorithm and survive IDB quota-eviction that
// otherwise silently empties Blob-backed entries on iOS Safari.
//
// Duck-types the Blob check via `.arrayBuffer()` so cross-realm Blob instances
// (e.g. Node's `buffer.Blob` in tests, service-worker cross-frame handoff) are
// also recognized as binary.
async function _partsToStorable(parts) {
  const out = {};
  if (!parts || typeof parts !== "object") return out;
  const looksLikeBlob = (v) => v && typeof v === "object" && typeof v.arrayBuffer === "function" && typeof v.size === "number";
  for (const [k, v] of Object.entries(parts)) {
    if (v == null) continue;
    if (looksLikeBlob(v)) {
      const ab = await v.arrayBuffer();
      out[k] = {
        __podBinary: true,
        ab,
        ct: v.type || "application/octet-stream",
        name: (v.name && typeof v.name === "string") ? v.name : `${k}.bin`,
        size: ab.byteLength,
      };
    } else {
      out[k] = v;
    }
  }
  return out;
}

// Rehydrate a storable parts map back into a raw {key: string | Blob} map
// right before we build the multipart FormData for upload.
function _storableToParts(storable) {
  const out = {};
  if (!storable || typeof storable !== "object") return out;
  for (const [k, v] of Object.entries(storable)) {
    if (v && typeof v === "object" && v.__podBinary && v.ab && v.ab.byteLength > 0) {
      out[k] = new File([v.ab], v.name || `${k}.bin`, { type: v.ct || "application/octet-stream" });
    } else {
      out[k] = v;
    }
  }
  return out;
}

// Describe missing/empty fields on a STORED (post-_partsToStorable) parts map.
// Uses `size` on the wrapped-binary payload so we don't rehydrate to inspect.
function _describeStoredDeliverParts(parts, requireSignature = true) {
  const missing = [];
  if (!parts || typeof parts !== "object") {
    return { missing: ["Comments", "Photo 1", "Photo 2", ...(requireSignature ? ["Signature"] : [])] };
  }
  if (!parts.comment || !String(parts.comment).trim()) missing.push("Comments");
  const binOk = (v) => v && typeof v === "object" && v.__podBinary && v.ab && v.ab.byteLength > 0;
  if (!binOk(parts.photo)) missing.push("Photo 1");
  if (!binOk(parts.photo2)) missing.push("Photo 2");
  if (requireSignature && !binOk(parts.signature)) missing.push("Signature");
  return { missing };
}

async function _listKeys() {
  const all = await keys();
  return all.filter((k) => typeof k === "string" && k.startsWith(STORE_PREFIX));
}

export async function listPending() {
  const ks = await _listKeys();
  const items = await Promise.all(ks.map((k) => get(k)));
  return items.filter(Boolean);
}

export async function remove(id) {
  await del(STORE_PREFIX + id);
}

// Nukes every queued POD/Fail submission. Exposed so the Driver page can
// give the operator a one-click escape hatch when the queue gets wedged by
// a bad payload or a server that's been down long enough that retries
// aren't going to fix anything.
export async function purgeAll() {
  const ks = await _listKeys();
  await Promise.all(ks.map((k) => del(k)));
  await notify();
}

// Returns dropped items so support / a driver-side "History" screen can
// inspect what got lost. Never fed to the sweeper.
export async function listDropped() {
  const allKeys = await keys();
  const items = [];
  for (const k of allKeys) {
    if (typeof k === "string" && k.startsWith(GRAVEYARD_PREFIX)) {
      const v = await get(k);
      if (v) items.push(v);
    }
  }
  items.sort((a, b) => (a.dropped_at || "").localeCompare(b.dropped_at || ""));
  return items;
}

// Requeues a dropped item back into the live queue (fresh attempts counter).
// Useful when the underlying error was actually transient — e.g. the driver
// re-logged-in after a 401 auth blip.
export async function requeueDropped(itemId) {
  const key = GRAVEYARD_PREFIX + itemId;
  const dead = await get(key);
  if (!dead) return false;
  const revived = { ...dead, status: "pending", attempts: 0, last_error: "requeued from graveyard" };
  delete revived.dropped_at;
  delete revived.dropped_reason;
  await set(STORE_PREFIX + itemId, revived);
  await del(key);
  await notify();
  return true;
}

// Subscribers — lets the Driver page show a "N pending uploads" badge and
// re-render after each sweep without polling IndexedDB itself.
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  // Fire once immediately so the badge has data on mount.
  listPending().then((items) => fn(items)).catch(() => {});
  return () => listeners.delete(fn);
}
async function notify() {
  if (!listeners.size) return;
  const items = await listPending();
  listeners.forEach((fn) => { try { fn(items); } catch { /* swallow */ } });
}

// Graveyard subscribers — fires ONCE per item the moment it is dropped
// (browser-storage corruption OR hard give-up after retries exhausted).
// Consumers use this to clear their optimistic UI + surface a red toast so
// the driver knows the delivery didn't reach the server. Without this the
// Driver page keeps showing a green checkmark on a stop the backend still
// thinks is open (PROD INCIDENT 2026-08-25, East 08-25 stops 3 & 4).
const graveyardListeners = new Set();
export function subscribeGraveyard(fn) {
  graveyardListeners.add(fn);
  return () => graveyardListeners.delete(fn);
}
function notifyGraveyard(dead) {
  if (!graveyardListeners.size) return;
  graveyardListeners.forEach((fn) => { try { fn(dead); } catch { /* swallow */ } });
}

// Uploader: a single function the consumer provides that knows how to POST
// one queue item. Resolves on success → we delete the item. Rejects on
// failure → we update attempts/last_error and keep it for the next sweep.
let uploaderFn = null;
let draining = false;
export function attachUploader(fn) {
  uploaderFn = fn;
  // Drain ASAP after attaching in case items survived a reload.
  void drain();
}

export async function drain() {
  if (!uploaderFn || draining) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    // No point hammering the network when the browser thinks we are offline.
    return;
  }
  draining = true;
  try {
    // Recover any items that were flagged "uploading" by a previous drain
    // that got interrupted (page navigation, tab close, browser crash mid-
    // POST). We hold the module-level `draining` lock so nothing else can be
    // in-flight; anything stuck on "uploading" here is stale and must be
    // retried. Without this, the "N uploads pending" badge is permanent.
    const preItems = await listPending();
    let recoveredAny = false;
    for (const it of preItems) {
      if (it.status === "uploading") {
        it.status = "pending";
        it.last_error = (it.last_error ? it.last_error + " · " : "") + "recovered from stuck uploading state";
        await set(STORE_PREFIX + it.id, it);
        recoveredAny = true;
      }
    }
    if (recoveredAny) await notify();

    const items = await listPending();
    for (const it of items) {
      // Skip anything that somehow flipped back to "uploading" during this
      // pass (shouldn't happen — the lock ensures no concurrent drains).
      if (it.status === "uploading") continue;
      // Corruption guard (2026-07-15 prod incident). Some browsers evict
      // Blob-bearing IDB records under storage pressure — the record still
      // exists, but its `parts` object comes back empty. Retrying such an
      // item produces an infinite loop of 400 "POD incomplete" server-side
      // and a stuck "1 upload pending" client-side. Dead-letter it with a
      // clear reason so support can see what happened.
      if (it.endpoint && it.endpoint.endsWith("/deliver")) {
        const shape = _describeStoredDeliverParts(it.parts, it.require_signature !== false);
        if (shape.missing.length) {
          const dead = {
            ...it,
            status: "dropped",
            dropped_at: new Date().toISOString(),
            dropped_reason: `browser-storage-loss: missing ${shape.missing.join(", ")}`,
          };
          try { await set(GRAVEYARD_PREFIX + it.id, dead); } catch { /* best-effort */ }
          await remove(it.id);
          console.warn("[podQueue] Dropping corrupted queue item (parts lost):", dead);
          notifyGraveyard(dead);
          continue;
        }
      }
      it.status = "uploading";
      it.last_try_at = new Date().toISOString();
      await set(STORE_PREFIX + it.id, it);
      await notify();
      try {
        await uploaderFn(it);
        await remove(it.id);
      } catch (e) {
        it.status = "failed";
        it.attempts += 1;
        // Prefer the FastAPI `detail` field so drivers see the real reason
        // (e.g. "POD storage temporarily unavailable (photo)") instead of
        // axios's generic "Request failed with status code 503". Falls back
        // through error.message → toString() for network-level failures.
        const serverDetail = e?.response?.data?.detail;
        const detailStr = typeof serverDetail === "string"
          ? serverDetail
          : (serverDetail ? JSON.stringify(serverDetail) : null);
        it.last_error = detailStr || (e && (e.message || e.toString())) || "unknown error";
        // Give up after ~10 minutes of retries only for truly permanent
        // client errors. AUTH errors (401/403) are NOT permanent — the
        // driver just needs to re-login; auto-purging would silently lose
        // their delivery record. 408/429 are always retryable.
        //
        // 400 is DELIBERATELY NOT in this list. In the 2026-07-10 incident,
        // transient POD-storage blips caused the backend to 400 with
        // "POD incomplete" and the queue auto-purged after 5 attempts,
        // losing the delivery record entirely. Backend now returns 503
        // for storage failures, so any 400 seen here is a genuine client
        // bug — the sweeper keeps the item so support can inspect it.
        const HARD_GIVEUP_ATTEMPTS = 20;         // ~10 min at 30 s sweeps
        const MIN_ATTEMPTS_BEFORE_GIVEUP = 5;    // safety floor
        const status = e?.response?.status;
        // 410 Gone = server explicitly says "this resource no longer exists"
        // (e.g. the order was deleted). Drop IMMEDIATELY — retrying 5 more
        // times cannot bring it back and just spams the "retrying in
        // background" toast. 404 keeps its safety floor because it can be
        // a transient caching issue.
        const permanentStatuses = new Set([404, 410, 422]);
        const instantDropStatuses = new Set([410]);
        const isInstantDrop = status && instantDropStatuses.has(status);
        const isPermanent = status && permanentStatuses.has(status) && it.attempts >= MIN_ATTEMPTS_BEFORE_GIVEUP;
        const isAuthError = status === 401 || status === 403;
        const shouldGiveUp = isInstantDrop || isPermanent || (it.attempts >= HARD_GIVEUP_ATTEMPTS && !isAuthError);
        if (shouldGiveUp) {
          // Preserve the dropped payload in a graveyard bucket so support can
          // recover the delivery details after the fact. Keeping the item
          // itself in the queue forever is what caused the earlier stuck-badge
          // bug, but nuking the record with no trace is what caused the
          // "delivered but never landed" incidents. Middle ground: separate
          // key prefix that doesn't feed the sweeper or the pending badge.
          const dead = { ...it, status: "dropped", dropped_at: new Date().toISOString(), dropped_reason: `${status || "network"}:${it.last_error}` };
          try {
            await set(GRAVEYARD_PREFIX + it.id, dead);
            console.warn("[podQueue] Dropping item after giving up:", dead);
          } catch { /* graveyard is best-effort */ }
          await remove(it.id);
          notifyGraveyard(dead);
        } else {
          await set(STORE_PREFIX + it.id, it);
          if (isAuthError) {
            console.warn(`[podQueue] Auth error (${status}) on upload — driver needs to re-login. Item kept for retry.`);
          }
        }
      } finally {
        await notify();
      }
    }
  } finally {
    draining = false;
  }
}

let sweepHandle = null;
export function startSweeper() {
  if (sweepHandle) return; // idempotent
  // Try immediately, then every SWEEP_MS, AND whenever the browser regains
  // connectivity / the tab returns to the foreground.
  void drain();
  sweepHandle = setInterval(() => { void drain(); }, SWEEP_MS);
  if (typeof window !== "undefined") {
    window.addEventListener("online", drain);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void drain();
    });
  }
}

export function stopSweeper() {
  if (sweepHandle) clearInterval(sweepHandle);
  sweepHandle = null;
}

// Helper: build a FormData from a queued `parts` map. Used by the uploader.
// The stored `parts` map may contain wrapped-binary tuples ({__podBinary,
// ab, ct, name}) that we rehydrate to real File instances just-in-time; we
// also still accept raw Blob/File for backwards-compat with items queued by
// older client bundles.
export function partsToFormData(parts) {
  const fd = new FormData();
  const src = _storableToParts(parts);
  for (const [k, v] of Object.entries(src)) {
    if (v == null) continue;
    if (v instanceof Blob || v instanceof File) {
      // Preserve the original filename for File parts, fall back to the key.
      const name = (v instanceof File && v.name) ? v.name : `${k}.bin`;
      fd.append(k, v, name);
    } else {
      fd.append(k, String(v));
    }
  }
  return fd;
}
